import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import LoginPage from "@/pages/LoginPage";
import MyTicketsPage from "@/pages/MyTicketsPage";
import NewTicketPage from "@/pages/NewTicketPage";
import TicketViewPage from "@/pages/TicketViewPage";
import DocsListPage from "@/pages/DocsListPage";
import DocViewPage from "@/pages/DocViewPage";

function useMe() {
  return useQuery({
    queryKey: ["portal-me"],
    queryFn: () => api<{ user: { id: string; display_name: string; kind: string } }>("/portal/me"),
    retry: false,
  });
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data, isLoading, isError } = useMe();
  if (isLoading) return <div className="p-8 text-slate-600">Loading…</div>;
  if (isError || !data?.user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function Header() {
  const navigate = useNavigate();
  const { data } = useMe();
  const logout = async () => {
    await api("/portal/auth/logout", { method: "POST" });
    navigate("/login", { replace: true });
  };
  return (
    <header className="border-b border-slate-200 bg-white px-6 py-3">
      <div className="mx-auto flex max-w-3xl items-center justify-between">
        <div className="font-semibold">Support portal</div>
        {data?.user ? (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-600">{data.user.display_name}</span>
            <button
              type="button"
              onClick={logout}
              className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-600 hover:bg-slate-100"
            >
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}

export default function App() {
  return (
    <div className="flex min-h-full flex-col">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 p-6">
        <Routes>
          <Route path="login" element={<LoginPage />} />
          <Route path="verify" element={<div className="text-slate-600">Signing you in…</div>} />
          <Route
            index
            element={
              <RequireAuth>
                <Navigate to="/my-tickets" replace />
              </RequireAuth>
            }
          />
          <Route
            path="my-tickets"
            element={
              <RequireAuth>
                <MyTicketsPage />
              </RequireAuth>
            }
          />
          <Route
            path="new"
            element={
              <RequireAuth>
                <NewTicketPage />
              </RequireAuth>
            }
          />
          <Route
            path="tickets/:publicId"
            element={
              <RequireAuth>
                <TicketViewPage />
              </RequireAuth>
            }
          />
          <Route
            path="docs"
            element={
              <RequireAuth>
                <DocsListPage />
              </RequireAuth>
            }
          />
          <Route
            path="docs/:docId"
            element={
              <RequireAuth>
                <DocViewPage />
              </RequireAuth>
            }
          />
        </Routes>
      </main>
    </div>
  );
}
